import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import TreeAPI from '../../../apis/TreeAPI';
import produce from 'immer';
import { SubscribeToCheckoutPage } from '../../../apis/Admin/Checkout';
import { Backdrop, Box, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { createGlobalStyle } from 'styled-components';
import VideoPlay from '../../VideoPlay';
import { CheckoutDialog } from './CheckoutDialog';
import { CheckoutSuccessDialog } from './CheckoutSuccessDialog';
import { CheckoutErrorDialog } from './CheckoutErrorDialog';
import { GetOrderSummary } from '../../../apis/User/indexv2';
import { CheckoutButtons } from './CheckoutButtons';
import moment from 'moment';


const CheckoutPage = () => {
    let { state } = useLocation();
    const history = useHistory();
    let publicId = typeof state === 'object' ? (state as any)?.publicId || "" : "";
    const [date, setDate] = useState(new Date());
    const [open, setOpen] = useState(true);
    const [openSuccess, setOpenSuccess] = useState<
        { open: boolean; payload?: { email: string; password?: string; } }
    >({
        open: false
    });
    const [openError, setOpenError] = useState(false);
    const [checkoutPage, setCheckoutPage] = useState<ICheckoutPage | 'NO_EXIST' | undefined>(undefined);

    const [episodesFull, setEpisodesFull] = useState<{
        [key: string]: IEpisode | undefined
    }>({});
    const [courses, setCourses] = useState<{
        [key: string]: ICourse | undefined
    }>({});

    const courseId = useMemo(() => {
        if (checkoutPage === undefined) return undefined;
        if (checkoutPage === 'NO_EXIST') return undefined;

        return checkoutPage.course.id
    }, [checkoutPage]);

    const course = useMemo(() => {
        if (courseId === undefined) return undefined;
        const courseObj = courses[courseId];
        if (courseObj === undefined) return undefined;

        return courseObj;
    }, [courseId, courses]);


    useEffect(() => {
        const unsubscribe = SubscribeToCheckoutPage(
            publicId,
            (checkoutPage) => {
                setCheckoutPage(checkoutPage);
            }
        );

        return () => unsubscribe();
    }, [publicId]);

    useEffect(() => {
        const unsubscribe = TreeAPI.subscribeToCourses(
            (course: ICourse) => {
                setCourses(
                    produce(draft => {
                        draft[course.id] = course;
                    })
                );
            },
            (id: string) => {
                setCourses(
                    produce(draft => {
                        draft[id] = undefined;
                    })
                );
            }
        );

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const unsubscribe = TreeAPI.subscribeToEpisodes(
            (episode: IEpisode) => {
                setEpisodesFull(
                    produce(draft => {
                        draft[episode.id] = episode;
                    })
                );
            },
            (id: string) => {
                setEpisodesFull(
                    produce(draft => {
                        draft[id] = undefined;
                    })
                );
            }
        );
        return () => unsubscribe();
    }, []);

    const episodesByCourse = useMemo(() => {
        if (course === undefined) return undefined;

        const episodesObj = course.episodes;
        if (episodesObj === undefined) return [];

        let arr: IEpisode[] = [];
        Object.keys(episodesObj).forEach(episodeKey => {
            if (
                typeof episodesObj[episodeKey] === 'boolean' &&
                episodesObj[episodeKey] === true
            ) {
                const episodeObj = episodesFull[episodeKey];
                if (episodeObj === undefined) return;
                arr.push(episodeObj);
            } else return;
        });

        arr.sort((a, b) => a.order - b.order);
        return arr;

    }, [episodesFull, course]);

    useEffect(() => {
        const id = setInterval(() => {
            setDate(new Date());
        }, 1000);
        return () => clearInterval(id);
    }, []);

    const totalCodeDiscountsLength = useMemo(() => {
        if (checkoutPage === undefined) return 0;
        if (checkoutPage === 'NO_EXIST') return 0;
        return checkoutPage.codes.filter(
            code => moment().isBetween(
                code.activation.dateFrom,
                code.activation.dateTo)).length;
    }, [checkoutPage, date]);

    const handleOnGetOrderSummary = useCallback(async (code: any) => {
        const result = await GetOrderSummary({
            publicId: publicId,
            code: code
        });
        // await sleep(3000);
        return {
            isCodeValid: result.isCodeValid,
            totalPrice: result.totalPrice,
            totalDiscountPercentPerCode: result.totalDiscountPercentPerCode,
            totalDiscountPercentPerTimer: result.totalDiscountPercentPerTimer,
            orderId: result.orderId,
            formCheckoutId: result.formCheckoutId,
            formCheckoutToken: result.formCheckoutToken
        };
    }, [publicId]);

    const handleOnClose = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (publicId === "") history.push("/");
    }, [publicId]);

    if (checkoutPage === 'NO_EXIST') return <Redirect to='/login' />;
    if (checkoutPage === undefined || episodesByCourse === undefined) return null;

    return (
        <>
            <CheckoutDialog
                open={open}
                hasAtLeastOneCupon={totalCodeDiscountsLength > 0}
                onClose={handleOnClose}
                onSuccess={payload => {
                    setOpen(false);
                    setOpenSuccess({
                        open: true,
                        payload: payload
                    });
                }}
                onFailure={() => {
                    setOpen(false);
                    setOpenError(true);
                }}
                getOrderSummary={handleOnGetOrderSummary} />
            <CheckoutSuccessDialog
                courseId={courseId}
                open={openSuccess.open}
                onClose={() => setOpenSuccess({ open: false })}
                payload={openSuccess.payload}
            />
            <CheckoutErrorDialog
                open={openError}
                onClose={() => setOpenError(false)}
                onReattempt={() => { 
                    setOpenError(false);
                    setOpen(true);
                }}
            />
        </>
    )
};

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #32b0ea;
  }
`

export default CheckoutPage;